import { createSelector } from "reselect";

import type { CheckoutReduxState } from "@app/types/checkout-redux-types";
import type { CheckoutReduxStateV2 } from "@app/types/checkout-v2-types";
import type { CurrencyCode } from "@app/types/currency";
import type { BookingCharges, CartCharges } from "@app/types/napi-charge-types";

const getRedeemableAmountsV2 = (
  state: Pick<CheckoutReduxStateV2, "redeemable_amounts">
) => {
  return state.redeemable_amounts;
};

const getCharges = (state: CheckoutReduxState) => state.cart.value.charges;

export type RedeemableAmount = {
  balance: number;
  currency: CurrencyCode;
};

export const getRedeemableAmountV2 = createSelector(
  [getRedeemableAmountsV2],
  redeemable_amounts => {
    if (!redeemable_amounts) {
      return null;
    }

    const { user_balance } = redeemable_amounts;

    const balance: number = user_balance?.value || 0;

    const currency: CurrencyCode = user_balance?.currency;
    return {
      balance,
      currency
    };
  }
);

export const getRedeemedAmount = createSelector(
  [getCharges],
  (charges: CartCharges | BookingCharges) => {
    const has_redeemed =
      isCartCharges(charges) &&
      (!!charges.loyalty_redeemed_amount || !!charges.wallet_redeemed_amount);

    return has_redeemed
      ? (charges.loyalty_redeemed_amount || 0) +
          (charges.wallet_redeemed_amount || 0)
      : 0;
  }
);

const isCartCharges = (
  charges: CartCharges | BookingCharges
): charges is CartCharges => {
  return (
    "loyalty_redeemed_amount" in charges || "wallet_redeemed_amount" in charges
  );
};
